import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { BadgeInvitation, ActionsInvitation, DialogLienInvitation } from "@/components/utilisateurs/Invitations";
import { LIBELLES_ACCES } from "@/components/employes/BlocStatutEmploye"; // 👥 E2
import {
  Users, UserPlus, Link, Unlink, Lock, Loader2, Mail, Shield,
  CheckCircle, Eye, EyeOff, Copy, RefreshCw, PowerOff, Power, Trash2, Camera, Unlock, MonitorSmartphone, History
} from "lucide-react";
import { DialogueAppareils } from "@/components/securite/Appareils"; // 🔐 Connexion P4
import { estProprietaire, aLeRole, rolesDe, libelleRole, ROLES_ATTRIBUABLES } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const token = () => localStorage.getItem("auth_token");
const headers = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

const ROLE_CONFIG = {
  proprietaire:  { label: "Propriétaire",          color: "bg-purple-100 text-purple-700", desc: "Accès total à toute la plateforme" },
  charge_projet: { label: "Chargée de Projet",     color: "bg-blue-100 text-blue-700",    desc: "Clients, projets, factures, calendrier" },
  adjoint_admin: { label: "Adjoint Administratif", color: "bg-indigo-100 text-indigo-700", desc: "Admin RH, employés, projets, calendrier" },
  chef_equipe:   { label: "Chef d'Équipe",          color: "bg-green-100 text-green-700",  desc: "Projets assignés, lavage, pointage, entrepôt" },
  peintre:       { label: "Peintre",                color: "bg-orange-100 text-orange-700", desc: "Pointage, projets assignés, bonus" },
  installateur:  { label: "Installateur LMDS",     color: "bg-teal-100 text-teal-700",    desc: "LMDS uniquement + Mon Espace" },
  representante: { label: "Représentante",          color: "bg-pink-100 text-pink-700",    desc: "Ventes, clients, rendez-vous" },
};

const INVITE_ONLY_ROLES = ["adjoint_admin", "chef_equipe", "peintre", "installateur", "representante", "charge_projet"];

export const GestionUtilisateurs = () => {
  const { isProprietaire, impersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(""); // ✅ OPTIMISATION: Recherche côté client
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [appareilsUser, setAppareilsUser] = useState(null); // 🔐 P4 : dialogue Appareils
  const [lienInvitation, setLienInvitation] = useState(null); // 🔐 P3 : { prenom, email, lien_invitation, expire_a, email_sent, renvoi }
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [inviteForm, setInviteForm] = useState({
    email: "", prenom: "", nom: "", role: "peintre", employee_id: "", send_email: true
  });
  const [linkEmployeeId, setLinkEmployeeId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  // 🔓 [v13.60.13 — 2026-08-15] Modale de confirmation pour déverrouiller
  //   un compte lockout. Ouverte via bouton visible uniquement quand
  //   user.compte_verrouille est truthy.
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [unlockingUserId, setUnlockingUserId] = useState(null);

  useEffect(() => { loadAll(); }, []);

  // 👥 E2 : ?inviter=<employee_id> ouvre le dialogue d'invitation pré-rempli (bouton « Inviter sur la plateforme » de la fiche)
  useEffect(() => {
    if (typeof window === 'undefined' || employees.length === 0) return;
    const id = new URLSearchParams(window.location.search).get('inviter');
    if (id && employees.some((e) => e.id === id)) { prefillFromEmployee(id); setShowInviteModal(true); }
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      const [usersRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/users`, { headers: headers() }),
        // 🔧 2026-05-23 — include_inactive: certains users sont liés à des employés
        // inactifs (ex: comptes historiques). On les affiche pour permettre le linking.
        api.getEmployees({ include_inactive: true })
      ]);
      const uData = usersRes.data;
      setUsers(uData.users || []);
      setEmployees(empRes.employees || []);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  const getLinkedEmployee = (user) => employees.find(e => e.id === user.employee_id);

  // Pré-remplir depuis la fiche employé sélectionnée
  const prefillFromEmployee = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return;
    // Déterminer le rôle à partir de la fiche employé
    let autoRole = 'peintre'; // défaut terrain
    if (emp.type_employe === 'admin') {
      autoRole = emp.role_admin === 'charge_projet' ? 'charge_projet' : 'adjoint_admin';
    } else if (emp.role_terrain === 'chef_equipe') {
      autoRole = 'chef_equipe';
    } else if (emp.role_terrain === 'sous_traitant' || emp.departement === 'LMDS') {
      autoRole = 'installateur';
    }
    setInviteForm(prev => ({
      ...prev, employee_id: empId,
      prenom: emp.prenom || prev.prenom,
      nom: emp.nom || prev.nom,
      email: emp.email || prev.email,
      role: autoRole,
    }));
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.prenom || !inviteForm.nom) {
      toast.error("Prénom, nom et email sont obligatoires"); return;
    }
    setSaving(true);
    try {
      const payload = { ...inviteForm, employee_id: inviteForm.employee_id || undefined };
      if (payload.employee_id) delete payload.role; // 👥 E3b — avec une fiche, les rôles viennent de la fiche (source)
      const res = await axios.post(`${API_URL}/api/auth/invite-employee`, payload, { headers: headers() });
      const data = res.data;
      if (res.status !== 200) throw new Error(data.detail || "Erreur");
      setShowInviteModal(false);
      setInviteForm({ email: "", prenom: "", nom: "", role: "chef_equipe", employee_id: "", send_email: true });
      setLienInvitation({ prenom: inviteForm.prenom, email: inviteForm.email, lien_invitation: data.lien_invitation, expire_a: data.expire_a, email_sent: data.email_sent });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  // 🔐 Connexion P3 — renvoyer / annuler une invitation
  const handleRenvoyerInvitation = async (user) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/invitations/${user.id}/renvoyer`, {}, { headers: headers() });
      setLienInvitation({ prenom: user.prenom, email: user.email, lien_invitation: data.lien_invitation, expire_a: data.expire_a, email_sent: data.email_sent, renvoi: true });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de renvoyer l'invitation."); }
  };
  const handleAnnulerInvitation = async (user) => {
    if (!window.confirm(`Annuler l'invitation de ${user.prenom} ${user.nom} ? Le compte sera désactivé et le lien ne fonctionnera plus.`)) return;
    try {
      await axios.post(`${API_URL}/api/auth/invitations/${user.id}/annuler`, {}, { headers: headers() });
      toast.success("Invitation annulée.");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible d'annuler l'invitation."); }
  };

  const handleLinkEmployee = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/auth/users/${selectedUser.id}`, { employee_id: linkEmployeeId || null }, { headers: headers() });
      if (res.status === 200) {
        toast.success(linkEmployeeId ? "✅ Lié à l'employé" : "✅ Lien supprimé");
        setShowLinkModal(false); setLinkEmployeeId(""); loadAll();
      } else { toast.error((res.data).detail || "Erreur"); }
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Min. 6 caractères"); return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/auth/users/${selectedUser.id}/password`, { nouveau_mot_de_passe: newPassword }, { headers: headers() });
      if (res.status === 200) { toast.success("✅ Mot de passe mis à jour"); setShowPwdModal(false); setNewPassword(""); }
      else toast.error("Erreur");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  // 👥 E3b : « Suspendre l'accès » / « Rétablir l'accès » avec confirmation
  const [confirmAcces, setConfirmAcces] = useState(null); // user
  // 👥 E3b : rôles d'un compte SANS fiche (sinon → fiche)
  const [rolesEdit, setRolesEdit] = useState(null); // { user, roles }
  const enregistrerRolesCompte = async () => {
    if (!rolesEdit) return;
    try {
      await axios.put(`${API_URL}/api/auth/users/${rolesEdit.user.id}`, { roles: rolesEdit.roles }, { headers: headers() });
      toast.success('Rôles enregistrés'); setRolesEdit(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const toggleActif = (user) => setConfirmAcces(user);
  const confirmerAcces = async () => {
    const user = confirmAcces;
    if (!user) return;
    try {
      const res = await axios.put(`${API_URL}/api/auth/users/${user.id}`, { actif: !user.actif }, { headers: headers() });
      if (res.status === 200) {
        toast.success(user.actif ? "Accès suspendu" : "Accès rétabli");
        (res.data?.avertissements || []).forEach((a) => toast.warning(a, { duration: 10000 })); // 👥 E1
        loadAll();
      }
    } catch { toast.error("Erreur"); }
    finally { setConfirmAcces(null); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${user.prenom} ${user.nom} (${user.email}) ?\n\nCette action est irréversible.`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/auth/users/${user.id}`, { headers: headers() });
      if (res.status === 200) { toast.success(`Compte de ${user.prenom} supprimé`); loadAll(); }
      else { const d = res.data; toast.error(d.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
  };

  const handleImpersonate = async (user) => {
    if (!user.employee_id) {
      toast.error("Cet utilisateur n'est pas lié à une fiche employé");
      return;
    }
    try {
      await impersonate(user.employee_id);
      toast.success(`Mode spectateur activé - Connecté comme ${user.prenom} ${user.nom}`);
      // Rediriger vers la page d'accueil pour voir l'expérience de l'employé
      window.location.href = '/';
    } catch (e) {
      toast.error(e.message || "Erreur lors de la connexion");
    }
  };

  const handleRequirePasswordChange = async (user) => {
    if (!window.confirm(`Forcer ${user.prenom} ${user.nom} à changer son mot de passe à sa prochaine connexion ?`)) return;
    try {
      const res = await axios.patch(`${API_URL}/api/auth/users/${user.id}/require-password-change`, {}, { headers: headers() });
      if (res.status === 200) {
        toast.success(res.data.message || "Statut mis à jour");
        loadAll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur réseau");
    }
  };

  // 🔓 [v13.60.13 — 2026-08-15] Déverrouille manuellement un compte lockout.
  //   Appelle POST /api/auth/users/{id}/unlock-account (owner + adjoint_admin).
  //   Utilisé quand un employé a atteint 5 échecs de login → 15 min de lock,
  //   ou après une réinit MDP qui aurait dû auto-déverrouiller mais ne l'a
  //   pas fait (pré-v13.60.13).
  const handleUnlockAccount = async () => {
    const user = users.find(u => u.id === unlockingUserId);
    if (!user) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/users/${user.id}/unlock-account`,
        {},
        { headers: headers() }
      );
      if (res.status === 200) {
        const { was_locked, recent_fails_purged } = res.data || {};
        toast.success(
          was_locked
            ? `✅ ${user.prenom} ${user.nom} a été déverrouillé (${recent_fails_purged} tentative(s) échouée(s) purgée(s))`
            : `Compte déjà déverrouillé — ${recent_fails_purged} tentative(s) échouée(s) purgée(s)`
        );
        setShowUnlockConfirm(false);
        setUnlockingUserId(null);
        loadAll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du déverrouillage");
    } finally {
      setSaving(false);
    }
  };

  const handleRequirePhotoUpload = async (user) => {
    if (!window.confirm(`Réinitialiser la photo de profil de ${user.prenom} ${user.nom} ?\nL'employé devra en téléverser une nouvelle à sa prochaine connexion.`)) return;
    try {
      const res = await axios.patch(`${API_URL}/api/auth/users/${user.id}/require-photo-upload`, {}, { headers: headers() });
      if (res.status === 200) {
        toast.success(res.data.message || "Photo réinitialisée");
        loadAll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur réseau");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  // ✅ OPTIMISATION: Filtre côté client pour recherche rapide
  const filteredUsers = searchQuery 
    ? users.filter(u => 
        `${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const activeUsers = filteredUsers.filter(u => u.actif);
  const inactiveUsers = filteredUsers.filter(u => !u.actif);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" /> Comptes & Accès
          </h1>
          <p className="text-slate-500 text-sm mt-1">{activeUsers.length} compte{activeUsers.length > 1 ? "s" : ""} actif{activeUsers.length > 1 ? "s" : ""} {searchQuery && `(${filteredUsers.length}/${users.length} affichés)`}</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto" data-testid="invite-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Inviter un employé
        </Button>
      </div>

      {/* ✅ OPTIMISATION: Barre de recherche */}
      <div className="relative">
        <Input
          placeholder="Rechercher par nom, prénom ou email..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-10"
        />
        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 flex gap-2">
        <Shield className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <strong>Rappel :</strong> Pour que l'employé accède à <strong>Mon Espace</strong> avec ses données, son compte doit être{" "}
          <strong>lié à sa fiche employé</strong> via le bouton <strong>🔗 Lier</strong>. Configurez les permissions de son rôle dans{" "}
          <a href="/gestion-acces" className="underline text-blue-700">Gestion des Permissions</a>.
        </div>
      </div>

      {/* Users list */}
      <div className="space-y-2">
        {[...activeUsers, ...inactiveUsers].map(user => {
          const linkedEmp = getLinkedEmployee(user);
          const rc = ROLE_CONFIG[user.role] || ROLE_CONFIG.chef_equipe;
          return (
            <Card key={user.id} className={`transition-all ${!user.actif ? "opacity-50" : ""}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <EmployeeAvatar photoUrl={linkedEmp?.photo_url} prenom={user.prenom || '?'} nom={user.nom || ''} size="md" />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{user.prenom} {user.nom}</p>
                        <Badge className={`text-xs ${rc.color}`} title="Rôle principal">{rc.label}</Badge>
                        {/* 👥 E3b — rôles multiples en chips + lien vers la fiche */}
                        {rolesDe(user).filter(r => r !== user.role).map(r => (
                          <Badge key={r} variant="outline" className="text-xs" data-testid={`chip-role-${user.id}-${r}`}>{libelleRole(r)}</Badge>
                        ))}
                        {isProprietaire && !estProprietaire(user) && (user.fiche?.employee_id
                          ? <a href={`/employes?employe=${user.fiche.employee_id}`} className="text-xs text-blue-600 hover:underline" data-testid={`lien-modifier-roles-${user.id}`}>Modifier dans la fiche</a>
                          : <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setRolesEdit({ user, roles: rolesDe(user) })} data-testid={`btn-modifier-roles-${user.id}`}>Modifier les rôles</button>)}
                        {user.must_change_password && <Badge className="bg-yellow-100 text-yellow-700 text-xs">Doit changer MDP</Badge>}
                        <BadgeInvitation user={user} />
                        {/* 👥 E2 — statut d'accès calculé + fiche inactive */}
                        {user.acces_plateforme && user.acces_plateforme !== 'invitation_en_attente' && (
                          <Badge className={`text-xs ${LIBELLES_ACCES[user.acces_plateforme]?.cls || ''}`} data-testid={`badge-acces-${user.id}`}>
                            {user.acces_plateforme === 'actif' ? 'Accès actif' : LIBELLES_ACCES[user.acces_plateforme]?.label || user.acces_plateforme}
                          </Badge>
                        )}
                        {user.fiche && user.fiche.est_actif === false && (
                          <Badge variant="outline" className="text-xs border-orange-300 text-orange-700" data-testid={`badge-fiche-inactive-${user.id}`}>Fiche inactive</Badge>
                        )}
                        {/* 🔒 [v13.60.13 — 2026-08-15] Badge lockout : compte verrouillé après 5 échecs de login */}
                        {user.compte_verrouille && (
                          <Badge className="bg-red-100 text-red-700 text-xs border border-red-300"
                                 data-testid={`badge-locked-${user.id}`}>
                            🔒 Verrouillé
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{user.email}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {linkedEmp ? (
                          <><Link className="w-3 h-3 text-green-500" /><span className="text-xs text-green-600">Lié à : <strong>{linkedEmp.prenom} {linkedEmp.nom}</strong></span></>
                        ) : (
                          <><Unlink className="w-3 h-3 text-orange-400" /><span className="text-xs text-orange-500">Aucune fiche employé liée</span></>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {isProprietaire && user.employee_id && !estProprietaire(user) && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleImpersonate(user)}
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                        title="Se connecter comme cet employé (Mode Spectateur)"
                        data-testid={`impersonate-btn-${user.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />Spectateur
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => { setSelectedUser(user); setLinkEmployeeId(user.employee_id || ""); setShowLinkModal(true); }}>
                      <Link className="w-3 h-3 mr-1" />{user.employee_id ? "Modifier lien" : "Lier"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setSelectedUser(user); setShowPwdModal(true); }}>
                      <Lock className="w-3 h-3 mr-1" />MDP
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAppareilsUser(user)} data-testid={`appareils-btn-${user.id}`}>
                      <MonitorSmartphone className="w-3 h-3 mr-1" />Appareils
                    </Button>
                    <Button size="sm" variant="outline" asChild data-testid={`historique-btn-${user.id}`}>
                      <a href={`/securite?onglet=historique&utilisateur=${user.id}`}><History className="w-3 h-3 mr-1" />Historique</a>
                    </Button>
                    {/* 🔓 [v13.60.13 — 2026-08-15] Bouton déverrouiller (visible si compte lockout) */}
                    {user.compte_verrouille && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50 font-semibold"
                        onClick={() => { setUnlockingUserId(user.id); setShowUnlockConfirm(true); }}
                        title="Déverrouiller ce compte lockout"
                        data-testid={`btn-unlock-${user.id}`}
                      >
                        <Unlock className="w-3 h-3 mr-1" />Déverrouiller
                      </Button>
                    )}
                    {isProprietaire && <ActionsInvitation user={user} onRenvoyer={handleRenvoyerInvitation} onAnnuler={handleAnnulerInvitation} />}
                    {isProprietaire && !user.must_change_password && !estProprietaire(user) && !['en_attente', 'annulee'].includes(user.invitation_statut) && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleRequirePasswordChange(user)}
                        className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                        title="Forcer le changement de mot de passe à la prochaine connexion"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />Réinit. MDP
                      </Button>
                    )}
                    {isProprietaire && user.employee_id && !estProprietaire(user) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRequirePhotoUpload(user)}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        title="Forcer l'upload d'une nouvelle photo à la prochaine connexion"
                        data-testid={`btn-reset-photo-${user.id}`}
                      >
                        <Camera className="w-3 h-3 mr-1" />Réinit. Photo
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      className={user.actif ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                      onClick={() => toggleActif(user)}
                      title={user.actif ? "Suspendre l'accès" : "Rétablir l'accès"}
                      data-testid={`btn-acces-${user.id}`}
                    >
                      {user.actif ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                    </Button>
                    {(user.fiche?.employee_id) && (
                      <Button size="sm" variant="outline" asChild data-testid={`btn-ouvrir-fiche-${user.id}`}>
                        <a href={`/employes?employe=${user.fiche.employee_id}`} title="Ouvrir la fiche employé">Ouvrir la fiche</a>
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      className="text-red-700 hover:bg-red-50 hover:text-red-800 border-red-200"
                      onClick={() => deleteUser(user)}
                      title="Supprimer définitivement ce compte"
                      data-testid={`btn-delete-user-${user.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* MODAL: Inviter */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" />Inviter un employé</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Lier depuis liste */}
            <div>
              <Label className="text-sm font-medium">Choisir depuis la liste des employés (optionnel)</Label>
              <Select value={inviteForm.employee_id || "none"} onValueChange={v => { if (v !== "none") prefillFromEmployee(v); else setInviteForm(p => ({ ...p, employee_id: "" })); }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner un employé pour pré-remplir..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Entrer manuellement —</SelectItem>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom} — {e.type_employe}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prénom *</Label>
                <Input value={inviteForm.prenom} onChange={e => setInviteForm(p => ({ ...p, prenom: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input value={inviteForm.nom} onChange={e => setInviteForm(p => ({ ...p, nom: e.target.value }))} className="mt-1" />
              </div>
            </div>

            <div>
              <Label>Courriel *</Label>
              <Input type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} className="mt-1" placeholder="employe@example.com" />
            </div>

            <div>
              <Label>Rôle *</Label>
              {inviteForm.employee_id && employees.find(e => e.id === inviteForm.employee_id) ? (
                <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid="invite-roles-fiche">
                  {rolesDe(employees.find(e => e.id === inviteForm.employee_id)).map(r => <Badge key={r} variant="outline" className="text-xs">{libelleRole(r)}</Badge>)}
                  <span className="text-xs text-slate-500">— rôles de la fiche (modifiables dans la fiche, section Emploi)</span>
                </div>
              ) : (
              <Select value={inviteForm.role} onValueChange={v => setInviteForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INVITE_ONLY_ROLES.map(r => (
                    <SelectItem key={r} value={r}>
                      <div>
                        <span className="font-medium">{ROLE_CONFIG[r]?.label}</span>
                        <span className="text-slate-500 text-xs ml-2">— {ROLE_CONFIG[r]?.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
              {!inviteForm.employee_id && inviteForm.role && ROLE_CONFIG[inviteForm.role] && (
                <p className="text-xs text-slate-500 mt-1.5">
                  <strong>Accès :</strong> {ROLE_CONFIG[inviteForm.role].desc}. Modifiable dans{" "}
                  <a href="/gestion-acces" className="text-blue-600 underline" target="_blank" rel="noreferrer">Gestion des Permissions</a>.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Switch checked={inviteForm.send_email} onCheckedChange={v => setInviteForm(p => ({ ...p, send_email: v }))} />
              <div>
                <p className="text-sm font-medium">Envoyer un email d'invitation</p>
                <p className="text-xs text-slate-500">L'employé recevra un courriel avec un lien « Choisir mon mot de passe ». Aucun mot de passe temporaire n'est généré.</p>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>Annuler</Button>
            <Button onClick={handleInvite} disabled={saving} className="bg-blue-600 text-white" data-testid="invite-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Créer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: Mot de passe temporaire affiché après création */}
      {/* 🔐 Connexion P3 — lien d'invitation affiché une seule fois (remplace le dialogue du mot de passe temporaire) */}
      <DialogLienInvitation info={lienInvitation} onFermer={() => setLienInvitation(null)} />

      {/* MODAL: Lier employé */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Lier à une fiche employé — {selectedUser?.prenom} {selectedUser?.nom}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Sélectionnez la fiche employé correspondante pour que cet utilisateur voie ses données dans <strong>Mon Espace</strong>.
            </p>
            <Select value={linkEmployeeId || "none"} onValueChange={v => setLinkEmployeeId(v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Sélectionner ou aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun lien</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom} — {e.type_employe}</SelectItem>)}
              </SelectContent>
            </Select>
            {linkEmployeeId && (() => { const emp = employees.find(e => e.id === linkEmployeeId); return emp ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded text-sm">
                ✅ <strong>{emp.prenom} {emp.nom}</strong> — {emp.type_employe} ({emp.departement})
              </div>
            ) : null; })()}
            {/* 🆕 2026-05-14 — Bouton de création automatique d'une fiche employé
                si l'utilisateur n'en a pas encore (utile pour propriétaires comme Éliot). */}
            {selectedUser && !selectedUser.employee_id && (
              <div className="border-t pt-3">
                <p className="text-xs text-slate-500 mb-2">
                  Aucune fiche employé existante ? Créez-en une automatiquement à partir de ce compte :
                </p>
                <Button
                  variant="outline"
                  className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  disabled={saving}
                  onClick={async () => {
                    if (!selectedUser) return;
                    setSaving(true);
                    try {
                      const res = await axios.post(
                        `${API_URL}/api/auth/users/${selectedUser.id}/create-link-employee`,
                        {},
                        { headers: headers() }
                      );
                      toast.success(`Fiche employé créée et liée ✓ (${res.data?.employee?.prenom} ${res.data?.employee?.nom})`);
                      setShowLinkModal(false);
                      setLinkEmployeeId("");
                      loadAll();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Erreur lors de la création");
                    } finally {
                      setSaving(false);
                    }
                  }}
                  data-testid="btn-create-link-employee"
                >
                  🆕 Créer & lier une nouvelle fiche employé
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkModal(false)}>Annuler</Button>
            <Button onClick={handleLinkEmployee} disabled={saving} className="bg-blue-600 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {linkEmployeeId ? "Lier l'employé" : "Supprimer le lien"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: Changer MDP */}
      <DialogueAppareils utilisateur={appareilsUser} open={!!appareilsUser} onClose={() => setAppareilsUser(null)} />
      <Dialog open={showPwdModal} onOpenChange={(open) => { setShowPwdModal(open); if (!open) { setNewPassword(""); setShowPassword(false); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" />Mot de passe — {selectedUser?.prenom} {selectedUser?.nom}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{selectedUser?.email}</p>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Nouveau mot de passe</Label>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-blue-600 px-2"
                  onClick={() => {
                    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
                    const pwd = Array.from({length: 10}, () => chars[Math.floor(Math.random()*chars.length)]).join('');
                    setNewPassword(pwd); setShowPassword(true);
                  }}>
                  <RefreshCw className="w-3 h-3 mr-1" />Générer auto
                </Button>
              </div>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" className="pr-16" />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5">
                  {newPassword && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { navigator.clipboard.writeText(newPassword); toast.success("Copié !"); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
            {newPassword && showPassword && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                Notez ce mot de passe avant de fermer cette fenêtre.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPwdModal(false)}>Annuler</Button>
            <Button onClick={handleChangePassword} disabled={saving || !newPassword || newPassword.length < 6} className="bg-blue-600 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🔓 [v13.60.13 — 2026-08-15] Modale de confirmation avant déverrouillage manuel */}
      {/* 👥 E3b — rôles d'un compte sans fiche */}
      <Dialog open={!!rolesEdit} onOpenChange={(o) => !o && setRolesEdit(null)}>
        <DialogContent data-testid="dialogue-roles-compte">
          <DialogHeader><DialogTitle>Rôles de {rolesEdit?.user?.prenom} {rolesEdit?.user?.nom}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Compte de gestion sans fiche employé : les rôles se modifient ici. Le premier rôle devient le rôle principal.</p>
          <div className="flex flex-wrap gap-2">
            {ROLES_ATTRIBUABLES.map(r => {
              const actif = rolesEdit?.roles?.includes(r);
              return (
                <button key={r} type="button" aria-pressed={actif} data-testid={`chip-compte-role-${r}`}
                  onClick={() => setRolesEdit(p => ({ ...p, roles: actif ? p.roles.filter(x => x !== r) : [...p.roles, r] }))}
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${actif ? 'bg-[#1a237e] text-white border-[#1a237e]' : 'bg-white text-slate-600 border-slate-300'}`}>{libelleRole(r)}</button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRolesEdit(null)}>Annuler</Button>
            <Button disabled={!rolesEdit?.roles?.length} onClick={enregistrerRolesCompte} data-testid="btn-enregistrer-roles-compte">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 👥 E2 — confirmation Suspendre / Rétablir l'accès */}
      <Dialog open={!!confirmAcces} onOpenChange={(o) => !o && setConfirmAcces(null)}>
        <DialogContent data-testid="dialogue-acces">
          <DialogHeader>
            <DialogTitle>{confirmAcces?.actif ? "Suspendre l'accès à la plateforme ?" : "Rétablir l'accès à la plateforme ?"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {confirmAcces?.actif
              ? `${confirmAcces?.prenom || ''} ${confirmAcces?.nom || ''} ne pourra plus se connecter et ses sessions seront fermées. La fiche employé n'est pas modifiée.`
              : `${confirmAcces?.prenom || ''} ${confirmAcces?.nom || ''} pourra de nouveau se connecter.${confirmAcces?.fiche?.est_actif === false ? " La fiche est inactive : le kiosque entrepôt restera refusé." : ""}`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAcces(null)} data-testid="dialogue-acces-annuler">Annuler</Button>
            <Button variant={confirmAcces?.actif ? "destructive" : "default"} onClick={confirmerAcces} data-testid="dialogue-acces-confirmer">
              {confirmAcces?.actif ? "Suspendre l'accès" : "Rétablir l'accès"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showUnlockConfirm} onOpenChange={(open) => {
        setShowUnlockConfirm(open);
        if (!open) setUnlockingUserId(null);
      }}>
        <DialogContent className="max-w-md" data-testid="dialog-unlock-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-red-600" />
              Déverrouiller ce compte ?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            {(() => {
              const u = users.find(x => x.id === unlockingUserId);
              return u ? (
                <>
                  <p className="text-slate-700">
                    Cette action va déverrouiller le compte de{" "}
                    <strong>{u.prenom} {u.nom}</strong> et remettre à zéro les
                    tentatives échouées récentes.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-xs">
                    L&apos;utilisateur pourra se reconnecter immédiatement avec
                    son mot de passe actuel.
                  </div>
                </>
              ) : null;
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowUnlockConfirm(false); setUnlockingUserId(null); }}
              disabled={saving}
              data-testid="btn-unlock-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={handleUnlockAccount}
              disabled={saving || !unlockingUserId}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-unlock-confirm"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Déverrouillage…</>
              ) : (
                <><Unlock className="w-4 h-4 mr-2" /> Déverrouiller</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
